import * as React from 'react';

import {IconAvatar} from 'shells/avatar/icon-avatar';
import {getChannelIconFromChannel} from 'shells/channels/helpers';
import {colors} from 'shells/colors/colors';
import {CoreEntityConfirmationDialog} from 'shells/core-entity-confirmation-dialog/core-entity-confirmation-dialog';
import {CirclePlusRegularIcon} from 'shells/icon/svg/circle-plus-regular-icon';
import {SourcesIcon} from 'shells/icon/svg/sources-icon';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {BaseRow} from 'shells/row/base-row/base-row';

import type {ChannelType} from 'nutshell-graphql-types';
import type {SourceFragment as Source} from 'nutshell-graphql-types';

import {ChannelRowReadState} from './channel-row-read-state';

type Props = {
    name: string;
    channelType: ChannelType;
    sources: Source[];
    sourceOptions: Source[];
    canAccessSetup: boolean;
    onSelectSource: (sourceId: string) => void;
    onRemoveChannel: (channel: ChannelType) => void;
    onRemoveSource: (sourceId: string) => void;
};

export function ChannelRow(props: Props) {
    const {onRemoveChannel} = props;

    const [isAddingChannelSource, setIsAddingChannelSource] = React.useState<boolean>(false);
    const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] = React.useState<boolean>(false);

    const removeButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    const ChannelIcon = getChannelIconFromChannel(props.channelType);

    const onAddSource = () => {
        setIsAddingChannelSource(true);
    };

    const onSelectSource = (sourceId: string) => {
        props.onSelectSource(sourceId);
        setIsAddingChannelSource(false);
    };

    const onConfirmRemoveChannel = React.useCallback(() => {
        setIsRemoveConfirmationOpen(false);
        onRemoveChannel(props.channelType);
    }, [props.channelType, onRemoveChannel]);

    const onCancelRemoveChannel = React.useCallback(() => {
        setIsRemoveConfirmationOpen(false);
    }, []);

    return (
        <>
            <div className='p-4'>
                <BaseRow
                    title={props.name}
                    type='channels'
                    customIcon={
                        <IconAvatar
                            icon={ChannelIcon ? ChannelIcon : SourcesIcon}
                            avatarSize={32}
                            iconSize={16}
                            iconColor={colors.grey}
                            backgroundColor={colors.offWhiteDk}
                            shape='square'
                        />
                    }
                    toolbarProps={{
                        options: [
                            {
                                id: 'add-source',
                                icon: CirclePlusRegularIcon,
                                tooltipText: 'Add source',
                                onClick: onAddSource,
                            },
                            {
                                id: 'remove',
                                icon: TrashRegularIcon,
                                tooltipText: 'Remove channel',
                                onClick: () => {
                                    setIsRemoveConfirmationOpen(true);
                                },
                                getRef: (ref) => {
                                    removeButtonRef.current = ref;
                                },
                            },
                        ],
                        forceHideToolbar: isAddingChannelSource,
                        forceShowToolbar: isRemoveConfirmationOpen,
                    }}
                >
                    <ChannelRowReadState
                        sources={props.sources}
                        sourceOptions={props.sourceOptions}
                        isAddingChannelSource={isAddingChannelSource}
                        onSelectSource={onSelectSource}
                        onCancel={() => {
                            setIsAddingChannelSource(false);
                        }}
                        onRemoveSource={props.onRemoveSource}
                        canAccessSetup={props.canAccessSetup}
                    />
                </BaseRow>
            </div>
            {isRemoveConfirmationOpen && (
                <CoreEntityConfirmationDialog
                    anchor={removeButtonRef.current}
                    type='channel'
                    parentEntity='lead'
                    onConfirm={onConfirmRemoveChannel}
                    onCancel={onCancelRemoveChannel}
                />
            )}
        </>
    );
}
